'use client'

import { type VariantProps, cva } from 'class-variance-authority'
import type * as React from 'react'

import { cn } from '@/shared/helpers/styles.helper'

import LogoTextSvg from './sd-lodo-text.svg'
import LogoIconSvg from './sd-logo-icon.svg'

// Ratios DS ajustés pour compenser les SVG séparés
// Gap: 34% hauteur icône (17px @ 50px)
// Text: 58% hauteur icône (compensation ratio largeur/hauteur du SVG texte)
const logoVariants = cva('inline-flex items-center shrink-0', {
  variants: {
    variant: {
      primary: '[&_.logo-icon]:text-primary [&_.logo-text]:text-foreground',
      monochrome: '[&_svg]:text-foreground',
      inverse: '[&_svg]:text-primary-foreground'
    },
    size: {
      // Tailles fixes — gap 34% hauteur icône, text 58% icône (ajusté)
      xs: 'gap-2 [&_.logo-icon]:h-6 [&_.logo-icon]:w-auto [&_.logo-text]:h-[14px] [&_.logo-text]:w-auto',
      sm: 'gap-2.5 [&_.logo-icon]:h-[30px] [&_.logo-icon]:w-auto [&_.logo-text]:h-[17px] [&_.logo-text]:w-auto',
      default: 'gap-3 [&_.logo-icon]:h-9 [&_.logo-icon]:w-auto [&_.logo-text]:h-[21px] [&_.logo-text]:w-auto',
      lg: 'gap-[17px] [&_.logo-icon]:h-[50px] [&_.logo-icon]:w-auto [&_.logo-text]:h-[29px] [&_.logo-text]:w-auto',
      // S'adapte à la largeur — ratios ajustés
      fill: 'w-full aspect-[244/50] gap-[6.97%] [&_.logo-icon]:h-full [&_.logo-icon]:w-auto [&_.logo-text]:h-[58%] [&_.logo-text]:w-auto'
    }
  },
  defaultVariants: {
    variant: 'primary',
    size: 'default'
  }
})

export interface LogoProps extends React.ComponentPropsWithoutRef<'div'>, VariantProps<typeof logoVariants> {
  /** Afficher uniquement l'icône (sans texte) */
  iconOnly?: boolean
  /**
   * Classes supplémentaires sur l'icône SVG.
   * Pour surcharger la couleur du variant, utiliser le modificateur `!` : `iconClassName="!text-white"`
   */
  iconClassName?: string
  /**
   * Classes supplémentaires sur le texte SVG.
   * Pour surcharger la couleur du variant, utiliser le modificateur `!` : `textClassName="!text-white"`
   */
  textClassName?: string
}

// Logo SeDomicilier (icône et texte), annoncé comme une image nommée « SeDomicilier »
function Logo({
  className,
  variant = 'primary',
  size = 'default',
  iconOnly = false,
  iconClassName,
  textClassName,
  ...props
}: LogoProps) {
  return (
    <div
      data-slot="logo"
      role="img"
      aria-label="SeDomicilier"
      className={cn(logoVariants({ variant, size, className }))}
      {...props}
    >
      <LogoIconSvg className={cn('logo-icon', iconClassName)} aria-hidden />
      {!iconOnly && <LogoTextSvg className={cn('logo-text', textClassName)} aria-hidden />}
    </div>
  )
}

export { Logo, logoVariants, LogoIconSvg as LogoIcon }
